// WebAuthn utility functions for Passkeys

export const isWebAuthnSupported = () => {
  return !!(navigator.credentials && navigator.credentials.create && navigator.credentials.get);
};

const base64urlToBase64 = (value) => {
  const base64 = String(value).replace(/-/g, '+').replace(/_/g, '/');
  return base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
};

const base64urlToArrayBuffer = (value) => {
  const binaryString = window.atob(base64urlToBase64(value));
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const arrayBufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const requireServerOptions = (options, purpose) => {
  if (!options?.challenge) {
    throw new Error(`Server-generated passkey ${purpose} options are required.`);
  }
};

const prepareCredentialDescriptors = (credentials = []) => {
  return credentials.map((credential) => ({
    ...credential,
    id: base64urlToArrayBuffer(credential.id),
  }));
};

const prepareCreationOptions = (options) => ({
  ...options,
  challenge: base64urlToArrayBuffer(options.challenge),
  user: {
    ...options.user,
    id: base64urlToArrayBuffer(options.user.id),
  },
  excludeCredentials: prepareCredentialDescriptors(options.excludeCredentials || []),
});

const prepareRequestOptions = (options) => ({
  ...options,
  challenge: base64urlToArrayBuffer(options.challenge),
  allowCredentials: prepareCredentialDescriptors(options.allowCredentials || []),
});

export const createCredential = async (options) => {
  requireServerOptions(options, 'registration');

  const credential = await navigator.credentials.create({
    publicKey: prepareCreationOptions(options),
  });

  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    response: {
      attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
      clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
      transports: credential.response.getTransports?.() || [],
    },
  };
};

export const getCredential = async (options) => {
  requireServerOptions(options, 'authentication');

  const assertion = await navigator.credentials.get({
    publicKey: prepareRequestOptions(options),
  });

  return {
    id: assertion.id,
    rawId: arrayBufferToBase64url(assertion.rawId),
    type: assertion.type,
    authenticatorAttachment: assertion.authenticatorAttachment || undefined,
    clientExtensionResults: assertion.getClientExtensionResults?.() || {},
    response: {
      authenticatorData: arrayBufferToBase64url(assertion.response.authenticatorData),
      clientDataJSON: arrayBufferToBase64url(assertion.response.clientDataJSON),
      signature: arrayBufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle ? arrayBufferToBase64url(assertion.response.userHandle) : null,
    },
  };
};

export const isConditionalUISupported = async () => {
  if (!isWebAuthnSupported()) return false;

  try {
    const available = await PublicKeyCredential.isConditionalMediationAvailable?.();
    return available || false;
  } catch {
    return false;
  }
};
